import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";

// Line-mask reveal (kept for backwards compatibility).
export function RevealLines({
	text,
	className = "",
	delay = 0,
	stagger = 0.09,
	as: Tag = "span",
	trigger = "onLoad",
	duration = 1.05,
}) {
	const lines = text.split("\n");
	const viewportProps =
		trigger === "inView"
			? { initial: "hidden", whileInView: "visible", viewport: { once: true, amount: 0.35 } }
			: { initial: "hidden", animate: "visible" };

	return (
		<Tag className={className}>
			{lines.map((line, i) => (
				<span
					className="block overflow-hidden"
					style={{ paddingBottom: "0.16em", marginBottom: "-0.08em" }}
					key={`${line}-${i}`}
				>
					<motion.span
						{...viewportProps}
						style={{ display: "inline-block", paddingBottom: "0.08em" }}
						variants={{
							hidden: { y: "110%" },
							visible: {
								y: "0%",
								transition: {
									duration,
									ease: [0.76, 0, 0.24, 1],
									delay: delay + i * stagger,
								},
							},
						}}
					>
						{line || "\u00A0"}
					</motion.span>
				</span>
			))}
		</Tag>
	);
}

// Word-by-word kinetic reveal — CTRL signature motion.
// NOTE: driven by a raw IntersectionObserver + animate prop. framer-motion 11.18's
// whileInView does not fire for these nested spans under React 19, which left
// headline text stuck hidden behind the line mask.
export function RevealWords({
	text,
	className = "",
	delay = 0,
	stagger = 0.06,
	duration = 0.9,
	trigger = "onLoad",
}) {
	const words = text.split(" ");
	const ref = useRef(null);
	const [inView, setInView] = useState(trigger !== "inView");

	useEffect(() => {
		if (trigger !== "inView" || inView) return;
		const el = ref.current;
		if (!el || typeof IntersectionObserver === "undefined") {
			setInView(true);
			return;
		}
		const io = new IntersectionObserver(
			(entries) => {
				if (entries[0].isIntersecting) {
					setInView(true);
					io.disconnect();
				}
			},
			{ threshold: 0.35 },
		);
		io.observe(el);
		return () => io.disconnect();
	}, [trigger, inView]);

	return (
		<span ref={ref} className={className}>
			{words.map((w, i) => (
				<span
					key={`${w}-${i}`}
					className="inline-block overflow-hidden align-bottom"
					style={{ paddingBottom: "0.14em", marginBottom: "-0.08em" }}
				>
					<motion.span
						initial={{ y: "110%" }}
						animate={inView ? { y: "0%" } : { y: "110%" }}
						transition={{ duration, ease: [0.76, 0, 0.24, 1], delay: delay + i * stagger }}
						style={{ display: "inline-block", paddingBottom: "0.08em" }}
					>
						{w}
					</motion.span>
					{i < words.length - 1 ? "\u00A0" : ""}
				</span>
			))}
		</span>
	);
}

// Individual reveal word (unused shim kept)
export function RevealWord({ children, delay = 0, className = "" }) {
	return (
		<span
			className={`inline-block overflow-hidden align-bottom ${className}`}
			style={{ paddingBottom: "0.14em", marginBottom: "-0.08em" }}
		>
			<motion.span
				initial={{ y: "110%" }}
				animate={{ y: "0%" }}
				transition={{ duration: 1.05, ease: [0.76, 0, 0.24, 1], delay }}
				style={{ display: "inline-block", paddingBottom: "0.08em" }}
			>
				{children}
			</motion.span>
		</span>
	);
}

export function FadeUp({ children, delay = 0, y = 24, className = "", once = true }) {
	return (
		<motion.div
			className={className}
			initial={{ opacity: 0, y }}
			whileInView={{ opacity: 1, y: 0 }}
			viewport={{ once, amount: 0.35 }}
			transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay }}
		>
			{children}
		</motion.div>
	);
}

// Counter component that ticks up on view.
export function Counter({ from = 0, to, duration = 1.6, className = "" }) {
	return (
		<motion.span
			className={className}
			initial={{ opacity: 0 }}
			whileInView={{ opacity: 1 }}
			viewport={{ once: true }}
			transition={{ duration: 0.4 }}
		>
			<motion.span
				initial={{ value: from }}
				whileInView={{ value: to }}
				viewport={{ once: true }}
				transition={{ duration, ease: [0.22, 1, 0.36, 1] }}
			>
				{(v) => (typeof v?.value === "number" ? Math.round(v.value) : to)}
			</motion.span>
		</motion.span>
	);
}
